import React, { useReducer, useContext, useEffect } from "react";
import PlanParticipation from "./PlanParticipation";
import ResponseRate from "../ces/ResponseRate";
import "../common/Insights.scss";
import Sll from "../sll/Sll";
import translate from "@/i18n/translate";
import { AppContext, AppContextInterface } from "../../contexts/AppContext";
import ReportingYears from "../common/elements/ReportingYears";
import ApiError from "../common/elements/ApiError";
import { checkIfCesProductIntegrated, checkIfSllProductIntegrated, getLoaderStatus } from "../common/helper";
import {
  ReportingYear,
  MembershipInterface,
  defaultReportingYear
} from "../common/interfaces/Interface";
const { REDIRECT_URI } = runConfig;

interface LoadedComponents {
  planParticipationPlans: boolean;
  planParticipationChartData: boolean;
  responseRate: boolean;
  sll: boolean;
}

interface ContinuousImprovementState {
  selectedReportingYear: ReportingYear;
  loadedComponents: LoadedComponents;
}

interface ContinuousImprovementAction {
  type: string;
  reportingYear: ReportingYear;
  component: string;
}

const defaultCIAction = {
  type: "",
  reportingYear: defaultReportingYear,
  component: ""
};

const reducer = (
  state: ContinuousImprovementState,
  action: ContinuousImprovementAction
) => {
  switch (action.type) {
    case "selectReportingYear":
      return {
        ...state,
        selectedReportingYear: action.reportingYear
      };
    case "setLoadedComponent":
      const component = action.component;
      let loadedComponents = state.loadedComponents;
      loadedComponents[component] = true;
      return { ...state, loadedComponents: loadedComponents };
    case "resetLoadedComponent":
      const newComponent = action.component;
      let newloadedComponents = state.loadedComponents;
      newloadedComponents[newComponent] = false;
      return { ...state, loadedComponents: newloadedComponents };
    case "resetAllLoadedComponent":
      return {
        ...state,
        loadedComponents: {
          planParticipationPlans: false,
          planParticipationChartData: false,
          responseRate: false,
          sll: false
        }
      };
    default:
      return state;
  }
};
const allComponentsLoaded = (
  loadedComponents: LoadedComponents,
  platformUuid?: string,
  showCourseEvaluation?: boolean,
  organizationView?: boolean
) => {
  if (organizationView) {
    return showCourseEvaluation ? loadedComponents.responseRate : true;
  }
  return (
    loadedComponents.planParticipationPlans &&
    loadedComponents.planParticipationChartData &&
    (showCourseEvaluation ? loadedComponents.responseRate : true) &&
    (platformUuid ? loadedComponents.sll : true)
  );
};
export interface ContinuousImprovementProps {
  reportingYears: ReportingYear[] | null;
  defaultReportingYear: ReportingYear;
  ryApiError: boolean;
  organizationView: boolean;
  productView?: "planning" | "navigator";
}
export default function ContinuousImprovement({
  reportingYears,
  defaultReportingYear,
  ryApiError,
  organizationView,
  productView = "planning"
}: ContinuousImprovementProps) {
  const { institution, membership, integratedProducts } =
    useContext<AppContextInterface>(AppContext);

  const initialState = {
    selectedReportingYear: defaultReportingYear,
    loadedComponents: {
      planParticipationPlans: false,
      planParticipationChartData: false,
      responseRate: false,
      sll: false
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const selectReportingYear = (year: ReportingYear) => {
    dispatch({ ...defaultCIAction, type: "resetAllLoadedComponent" });
    dispatch({
      ...defaultCIAction,
      type: "selectReportingYear",
      reportingYear: year
    });
  };

  useEffect(() => {
    selectReportingYear(defaultReportingYear);
  }, [defaultReportingYear]);

  const onComponentLoad = (component: string) => {
    dispatch({
      ...defaultCIAction,
      type: "setLoadedComponent",
      component: component
    });
  };

  const resetLoadedComponent = (component: string) => {
    dispatch({
      ...defaultCIAction,
      type: "resetLoadedComponent",
      component: component
    });
  };

  useEffect(() => {
    const liveRegion = document.querySelector(".continuous-improvement-status");
    institution &&
      getLoaderStatus(
        !allComponentsLoaded(
          state.loadedComponents,
          institution.platformUuid,
          checkIfCesProductIntegrated(integratedProducts),
          organizationView
        ),
        liveRegion
      );
  }, [
    institution &&
    allComponentsLoaded(
      state.loadedComponents,
      institution.platformUuid,
      checkIfCesProductIntegrated(integratedProducts),
      organizationView
    ),
    state.selectedReportingYear
  ]);

  const continuousImprovementWidgets = () => {
    return (
      <div>
        <span
          className="sr-only continuous-improvement-status"
          role="status"
          aria-live="assertive"
        ></span>
        <div className="continuous-improvement-widgets">
          {!organizationView && institution && institution.isPssIntegrated && (
            <div
              className={
                institution && checkIfCesProductIntegrated(integratedProducts)
                  ? "multiple-widget"
                  : ""
              }
            >
              <PlanParticipation
                selectedReportingYear={state.selectedReportingYear}
                onComponentLoad={onComponentLoad}
                resetLoadedComponent={resetLoadedComponent}
                productView={productView}
                key={state.selectedReportingYear.uuid}
              />
            </div>
          )}
          {institution && checkIfCesProductIntegrated(integratedProducts) && (
            <div
              className={
                institution && institution.isPssIntegrated
                  ? "multiple-widget"
                  : ""
              }
            >
              <ResponseRate
                selectedReportingYear={state.selectedReportingYear}
                onComponentLoad={onComponentLoad}
                resetLoadedComponent={resetLoadedComponent}
                organizationView={organizationView}
              />
            </div>
          )}
        </div>
        {!organizationView && checkIfSllProductIntegrated(integratedProducts) && (
          <Sll
            selectedReportingYear={state.selectedReportingYear}
            onComponentLoad={onComponentLoad}
          />
        )}
      </div>
    );
  };

  return (
    <section
      className="body wm-grid tab-body-home"
      id="section_insights"
    >
      <div>
        <section className="insights">
          <div className="institution-name -bottom flex-row -justified sse-desc flex-col-sm">
            <h3 className="helper-label helper-default-margin">
              {translate("app.continuousImprovement.title")}
            </h3>
            {reportingYears &&
              reportingYears?.length > 0 &&
              state.selectedReportingYear.startDate && (
                <ReportingYears
                  reportingYears={reportingYears}
                  selectedReportingYear={state.selectedReportingYear}
                  selectReportingYear={selectReportingYear}
                  key={state.selectedReportingYear.uuid}
                />
              )}
          </div>

          {reportingYears &&
            reportingYears?.length > 0 &&
            continuousImprovementWidgets()}

          {reportingYears?.length === 0 &&
            membership &&
            academicYearEmptyState(membership)}
          {ryApiError && <ApiError />}
        </section>
      </div>
    </section>
  );
}

const academicYearEmptyState = (membership: MembershipInterface) => {
  return (
    <div className="academic-yr-empty-state flex-row">
      <div>
        <div className="empty-state-title">
          {translate("app.continuousImprovement.noAcademicYearTitle")}
        </div>
        {membership.userRole === "admin" && adminEmptyState()}
        {membership.userRole === "contributor" && contributorEmptyState()}
      </div>
    </div>
  );
};

function contributorEmptyState() {
  return (
    <div className="empty-state-text">
      {translate("app.continuousImprovement.noAcademicYearText")}
    </div>
  );
}

function adminEmptyState() {
  return (
    <div className="empty-state-text">
      <a
        className="empty-state-link"
        href={`${REDIRECT_URI}/admin/configuration_settings/reporting_year`}
      >
        {translate("app.continuousImprovement.createReportingYear")}
      </a>
      {translate("app.continuousImprovement.noAcademicYearSubText")}
    </div>
  );
}
